/**
 * Preset countdown timers — the long-tail SEO surface ("5 minute timer", etc.).
 * Generic presets share parameterized copy keyed on their human label; Pomodoro
 * is a custom entry with its own content. One dynamic `$preset` route renders
 * any entry, so adding "12-minute-timer" later is a one-line addition here.
 */
import { m } from "@/paraglide/messages";

export interface PresetFaq {
  q: () => string;
  a: () => string;
}

export interface TimerPreset {
  slug: string;
  path: string;
  seconds: number;
  navName: () => string;
  metaTitle: () => string;
  metaDescription: () => string;
  h1: () => string;
  intro: () => string;
  howTo: () => string;
  faqs: PresetFaq[];
  /** Duration-specific "common uses" list — the unique content per preset. */
  uses?: Array<() => string>;
}

const BASE = "/timer";

/** Per-preset unique copy: without it, every preset page is the same template
 * with only the number swapped — the thin/doorway-content pattern. */
interface PresetUnique {
  howTo: () => string;
  uses: Array<() => string>;
  extraFaqs: PresetFaq[];
}

/**
 * Locale-aware preset label ("5 Minutes" / "5 Minuten" / "5分"). Must be called
 * lazily (inside message closures), never at module scope — the active locale
 * is per-request during SSR.
 */
function localizedLabel(seconds: number): string {
  if (seconds >= 3600) return m.timer_label_1hour();
  const n = seconds / 60;
  return n === 1 ? m.timer_label_1minute() : m.timer_label_minutes({ n });
}

function generic(
  slug: string,
  seconds: number,
  unique: PresetUnique,
): TimerPreset {
  const label = () => localizedLabel(seconds);
  return {
    slug,
    path: `${BASE}/${slug}`,
    seconds,
    navName: label,
    metaTitle: () => m.timer_preset_meta_title({ label: label() }),
    metaDescription: () => m.timer_preset_meta_description({ label: label() }),
    h1: () => m.timer_preset_h1({ label: label() }),
    intro: () => m.timer_preset_intro({ label: label() }),
    howTo: unique.howTo,
    uses: unique.uses,
    faqs: [
      {
        q: () => m.timer_preset_faq1_q({ label: label() }),
        a: () => m.timer_preset_faq1_a({ label: label() }),
      },
      { q: () => m.timer_preset_faq2_q(), a: () => m.timer_preset_faq2_a() },
      ...unique.extraFaqs,
    ],
  };
}

/** Builds the PresetUnique for one preset from its message-key prefix. */
function unique(
  howTo: () => string,
  uses: Array<() => string>,
  faq3: PresetFaq,
  faq4: PresetFaq,
): PresetUnique {
  return { howTo, uses, extraFaqs: [faq3, faq4] };
}

const pomodoro: TimerPreset = {
  slug: "pomodoro-timer",
  path: `${BASE}/pomodoro-timer`,
  seconds: 1500,
  navName: () => m.timer_pomodoro_nav(),
  metaTitle: () => m.timer_pomodoro_meta_title(),
  metaDescription: () => m.timer_pomodoro_meta_description(),
  h1: () => m.timer_pomodoro_h1(),
  intro: () => m.timer_pomodoro_intro(),
  howTo: () => m.timer_pomodoro_howto(),
  uses: [
    () => m.timer_pomodoro_use1(),
    () => m.timer_pomodoro_use2(),
    () => m.timer_pomodoro_use3(),
  ],
  faqs: [
    { q: () => m.timer_pomodoro_faq1_q(), a: () => m.timer_pomodoro_faq1_a() },
    { q: () => m.timer_pomodoro_faq2_q(), a: () => m.timer_pomodoro_faq2_a() },
    { q: () => m.timer_pomodoro_faq3_q(), a: () => m.timer_pomodoro_faq3_a() },
    { q: () => m.timer_pomodoro_faq4_q(), a: () => m.timer_pomodoro_faq4_a() },
  ],
};

export const TIMER_PRESETS: Record<string, TimerPreset> = {
  "1-minute-timer": generic(
    "1-minute-timer",
    60,
    unique(
      () => m.timer_u1m_howto(),
      [
        () => m.timer_u1m_use1(),
        () => m.timer_u1m_use2(),
        () => m.timer_u1m_use3(),
      ],
      { q: () => m.timer_u1m_faq3_q(), a: () => m.timer_u1m_faq3_a() },
      { q: () => m.timer_u1m_faq4_q(), a: () => m.timer_u1m_faq4_a() },
    ),
  ),
  "2-minute-timer": generic(
    "2-minute-timer",
    120,
    unique(
      () => m.timer_u2m_howto(),
      [
        () => m.timer_u2m_use1(),
        () => m.timer_u2m_use2(),
        () => m.timer_u2m_use3(),
      ],
      { q: () => m.timer_u2m_faq3_q(), a: () => m.timer_u2m_faq3_a() },
      { q: () => m.timer_u2m_faq4_q(), a: () => m.timer_u2m_faq4_a() },
    ),
  ),
  "3-minute-timer": generic(
    "3-minute-timer",
    180,
    unique(
      () => m.timer_u3m_howto(),
      [
        () => m.timer_u3m_use1(),
        () => m.timer_u3m_use2(),
        () => m.timer_u3m_use3(),
      ],
      { q: () => m.timer_u3m_faq3_q(), a: () => m.timer_u3m_faq3_a() },
      { q: () => m.timer_u3m_faq4_q(), a: () => m.timer_u3m_faq4_a() },
    ),
  ),
  "5-minute-timer": generic(
    "5-minute-timer",
    300,
    unique(
      () => m.timer_u5m_howto(),
      [
        () => m.timer_u5m_use1(),
        () => m.timer_u5m_use2(),
        () => m.timer_u5m_use3(),
      ],
      { q: () => m.timer_u5m_faq3_q(), a: () => m.timer_u5m_faq3_a() },
      { q: () => m.timer_u5m_faq4_q(), a: () => m.timer_u5m_faq4_a() },
    ),
  ),
  "10-minute-timer": generic(
    "10-minute-timer",
    600,
    unique(
      () => m.timer_u10m_howto(),
      [
        () => m.timer_u10m_use1(),
        () => m.timer_u10m_use2(),
        () => m.timer_u10m_use3(),
      ],
      { q: () => m.timer_u10m_faq3_q(), a: () => m.timer_u10m_faq3_a() },
      { q: () => m.timer_u10m_faq4_q(), a: () => m.timer_u10m_faq4_a() },
    ),
  ),
  "15-minute-timer": generic(
    "15-minute-timer",
    900,
    unique(
      () => m.timer_u15m_howto(),
      [
        () => m.timer_u15m_use1(),
        () => m.timer_u15m_use2(),
        () => m.timer_u15m_use3(),
      ],
      { q: () => m.timer_u15m_faq3_q(), a: () => m.timer_u15m_faq3_a() },
      { q: () => m.timer_u15m_faq4_q(), a: () => m.timer_u15m_faq4_a() },
    ),
  ),
  "20-minute-timer": generic(
    "20-minute-timer",
    1200,
    unique(
      () => m.timer_u20m_howto(),
      [
        () => m.timer_u20m_use1(),
        () => m.timer_u20m_use2(),
        () => m.timer_u20m_use3(),
      ],
      { q: () => m.timer_u20m_faq3_q(), a: () => m.timer_u20m_faq3_a() },
      { q: () => m.timer_u20m_faq4_q(), a: () => m.timer_u20m_faq4_a() },
    ),
  ),
  "30-minute-timer": generic(
    "30-minute-timer",
    1800,
    unique(
      () => m.timer_u30m_howto(),
      [
        () => m.timer_u30m_use1(),
        () => m.timer_u30m_use2(),
        () => m.timer_u30m_use3(),
      ],
      { q: () => m.timer_u30m_faq3_q(), a: () => m.timer_u30m_faq3_a() },
      { q: () => m.timer_u30m_faq4_q(), a: () => m.timer_u30m_faq4_a() },
    ),
  ),
  "45-minute-timer": generic(
    "45-minute-timer",
    2700,
    unique(
      () => m.timer_u45m_howto(),
      [
        () => m.timer_u45m_use1(),
        () => m.timer_u45m_use2(),
        () => m.timer_u45m_use3(),
      ],
      { q: () => m.timer_u45m_faq3_q(), a: () => m.timer_u45m_faq3_a() },
      { q: () => m.timer_u45m_faq4_q(), a: () => m.timer_u45m_faq4_a() },
    ),
  ),
  "1-hour-timer": generic(
    "1-hour-timer",
    3600,
    unique(
      () => m.timer_u1h_howto(),
      [
        () => m.timer_u1h_use1(),
        () => m.timer_u1h_use2(),
        () => m.timer_u1h_use3(),
      ],
      { q: () => m.timer_u1h_faq3_q(), a: () => m.timer_u1h_faq3_a() },
      { q: () => m.timer_u1h_faq4_q(), a: () => m.timer_u1h_faq4_a() },
    ),
  ),
  "pomodoro-timer": pomodoro,
};

export const TIMER_PRESET_LIST: TimerPreset[] = Object.values(TIMER_PRESETS);

/**
 * Safe lookup by slug. Uses `Object.hasOwn` so inherited properties
 * ("constructor", "toString", "__proto__", …) resolve to undefined instead of
 * an Object.prototype member — otherwise a URL like /timer/constructor would
 * slip past the notFound() guard and crash the page.
 */
export function getPreset(slug: string): TimerPreset | undefined {
  return Object.hasOwn(TIMER_PRESETS, slug) ? TIMER_PRESETS[slug] : undefined;
}
